<template>
  <DSSidebarGroup
    v-model:expanded="expanded"
    :icon="group.icon"
    :label="group.label || ''"
    :badge="group.badge"
    :active="!!group.active"
    :is-mobile
  >
    <template #menu>
      <draggable
        :model-value="orderedItems"
        item-key="key"
        handle="[data-handle]"
        :animation="200"
        :disabled="isEditMode"
        class="space-y-1"
        @update:model-value="updateItems"
      >
        <template #item="{ element: child }">
          <NavbarGroupMenuRow
            :child
            :is-dashboard="isDashboardGroup && !isReadonly"
            :is-edit-mode
            @select="handleClickChild(child)"
          />
        </template>
        <template
          v-if="isDashboardGroup"
          #footer
        >
          <DSDropdownMenuSeparator />
          <AddViewButton v-slot="{ icon, label, onClick }">
            <DSButton
              type="button"
              variant="ghost"
              size="sm"
              class="w-full px-2 justify-start"
              @click="emit('click'); onClick()"
            >
              <component
                :is="icon"
                class="size-3.5 shrink-0"
                aria-hidden="true"
              />
              {{ label }}
            </DSButton>
          </AddViewButton>
        </template>
      </draggable>
    </template>
    <template #children>
      <draggable
        :model-value="orderedItems"
        item-key="key"
        handle="[data-handle]"
        :animation="200"
        :disabled="isEditMode"
        class="ml-4.5 flex flex-col gap-px border-l border-white/15 dark:border-white/10 pl-2.5 mb-4"
        @update:model-value="updateItems"
      >
        <template #item="{ element: child }">
          <NavbarGroupChildRow
            :child
            :is-dashboard="isDashboardGroup && !isReadonly"
            :is-edit-mode
            :is-mobile
            @click="emit('click');"
          />
        </template>
        <template #footer>
          <AddViewButton
            v-if="isDashboardGroup"
            v-slot="{ icon, label, onClick }"
          >
            <button
              type="button"
              class="mt-0.5 flex w-full items-center gap-3 rounded-ds-sm hover:bg-white/10 border border-dashed border-primary-foreground/30 px-2.5 text-sm text-primary-foreground cursor-pointer"
              :class="isMobile ? 'h-11' : 'h-8'"
              @click="emit('click'); onClick()"
            >
              <component
                :is="icon"
                class="size-3.5 shrink-0"
                aria-hidden="true"
              />
              {{ label }}
            </button>
          </AddViewButton>
        </template>
      </draggable>
    </template>
  </DSSidebarGroup>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { useRouter } from 'vue-router'
import draggable from 'vuedraggable'
import { orderBy } from 'lodash'

import { NavigationItem } from '@types'

import { DASHBOARD_NAME } from '@/const'

import { orderTabs } from '@/store/repositories/utils/helpers'

import { useDashboardsStore } from '@/store/dashboards'
import { useNavigationStore } from '@/store/navigation'
import { useRepositoriesStore } from '@/store/repositories'

import AddViewButton from '@/views/Dashboards/components/AddViewButton.vue'

import { DSButton, DSDropdownMenuSeparator, DSSidebarGroup } from '@ds'

import NavbarGroupMenuRow from './NavbarGroupMenuRow.vue'
import NavbarGroupChildRow from './NavbarGroupChildRow.vue'

type Props = {
  group: NavigationItem
  isMobile?: boolean
  isReadonly?: boolean
}

const { group } = defineProps<Props>()

const emit = defineEmits<{
  (e: 'click'): void
}>()

const router = useRouter()

const navigationStore = useNavigationStore()
const repositoriesStore = useRepositoriesStore()
const dashboardsStore = useDashboardsStore()

const expanded = ref(false)
const isEditMode = computed(() => dashboardsStore.getEditMode)
const isDashboardGroup = computed(() => group.label === DASHBOARD_NAME)

const items = computed(
  () =>
    (group.label &&
      navigationStore.getChildNavigation(group.label)) ||
    [],
)

const order = computed(() => repositoriesStore.getSortedTabs)

const orderedItems = computed(() =>
  orderBy(items.value, (item, index) => orderTabs(order.value, `${item.key}`, index)),
)

const updateItems = (items: NavigationItem[]) => {
  repositoriesStore.sortTabs(items.map(item => `${item.key}`))
}

const handleClickChild = (child: NavigationItem) => {
  router.push({ name: child.route, params: child.routeParams })
  emit('click')
}
</script>
